import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from '~/hooks/useTranslation'
import { useState } from 'react'
import { hsvaToHex } from '@uiw/color-convert'

const useServiceRequestForm = ({
  setPage,
  setShowBackButton,
  current_user,
  serviceRequestType,
  meetingViaCalendly,
  createServiceRequest,
  serviceRequest,
}) => {
  const { t } = useTranslation('schedule_modal')

  const [submitted, setSubmitted] = useState(false)

  const handleNext = () => setPage((prevPage) => prevPage + 1)
  const handleBack = () => {
    setPage((prevPage) => prevPage - 1)
    setSubmitted(false)
  }

  const message = t('complete_form_tooltip')
  const linkedin_url = z.string().min(1, { message })
  const example_job_1 = z.string()
  const example_job_2 = z.string()
  const location = z.string().min(1, { message })
  const industry = z.string().min(1, { message })

  const hsvaSchema = z.object({
    h: z.number(),
    s: z.number(),
    v: z.number(),
    a: z.number(),
  })
  const schemaMap = {
    'ServiceRequest::LinkedInReview': {
      linkedin_url,
    },
    'ServiceRequest::OrientationCall': {},
    'ServiceRequest::BoardSearch': {},
    'ServiceRequest::ResumeReview': {
      document_id: z.union([z.string(), z.number()]),
      resume_document_id: z.union([z.string(), z.number()]),
      seniority_level: z.string().min(1, { message }),
      linkedin_url: z.string().optional(),
      example_job_1,
    },
    UploadNewResume: {
      document_id: z.string().min(1, { message }),
      resume_document_id: z.string().min(1, { message }),
    },
    'ResumeRevision::CandidateRevision': {
      notes: z.string().min(1, { message }),
      remote_content: z.instanceof(File).optional().or(z.literal(null)), // Allow null or omitted
    },
    'ServiceRequest::JobSearchStrategy': {
      desired_goal: z.string().min(1, { message }),
      desired_timeline: z.string().min(1, { message }),
    },
    'ServiceRequest::InterviewPrep': {
      target_roles: z.string().min(1, { message }),
      career_level: z.string().min(1, { message }),
      interview_types: z.array(z.string()).min(1, { message }), // <-- updated line
      message: z.string().min(1, { message }),
    },
    'ServiceRequest::JobSearchBurnout': {},
    'ServiceRequest::StrengthsAssessment': {
      document_id: z.any(),
    },
    'ServiceRequest::ExecutiveBio': {
      linkedin_url,
      document_id: z.union([z.string(), z.number()]),
      resume_document_id: z.union([z.string(), z.number()]),
      message: z.string().optional(),
    },
    'ServiceRequest::RecruiterGuidanceSupport': {
      industry_role: z.string().min(1, { message }),
      location,
      industry,
      salary: z.string().min(1, { message }),
    },
    'ServiceRequest::TargetCompanyResearch': {
      industry,
      industry_2: z.string(),
      sector: z.string().min(1, { message }),
      example_job_1,
      example_job_2,
      company_size: z.string().min(1, { message }),
      location,
    },
    'ServiceRequest::MockInterview': {
      target_job_description: z.string().min(1, { message }),
      resume_document_id: z.union([z.string(), z.number()]),
      interview_with: z.string(),
      interview_type: z.string(),
    },
    'ServiceRequest::CoverLetterReview': {
      candidate_document: z.any(),
      resume_document_id: z.any(),
      target_job_description: z.string().optional(),
      message: z.string().optional(),
    },
    'ServiceRequest::ResumeTailoring': {
      target_job_description: z.string().min(1, { message }),
      resume_document_id: z.union([z.string(), z.number()]),
    },
    'ServiceRequest::ResumeWriting': {
      target_job_description: z.string().min(1, { message }),
      work_experience: z.string().min(1, { message }),
      education_history: z.string().min(1, { message }),
      linkedin_url: z.string().optional(),
      achievements: z.string().optional(),
      skills_certifications: z.string().optional(),
      resume_document_id: z.union([z.string(), z.number()]).optional(),
    },
    'ServiceRequest::CareerPivotPlanning': {
      most_recent_role: z.string().optional(),
      desired_role: z.string().optional(),
      message: z.string().min(1, { message }),
    },
    'ServiceRequest::GeneralCheckIn': {
      general_topic: z.string().min(1, { message }),
      message: z.string().min(1, { message }),
    },
    'ServiceRequest::TargetedResearch': {
      industry,
      industry_2: z.string(),
      sector: z.string().min(1, { message }),
      example_job_1,
      example_job_2,
      example_company_1: z.string(),
      example_company_2: z.string(),
      company_size: z.string().min(1, { message }),
      location,
    },
    'ServiceRequest::SalaryResearch': {
      industry,
      industry_2: z.string(),
      sector: z.string().min(1, { message }),
      example_job_1,
      example_job_2,
      company_size: z.string().min(1, { message }),
      location,
    },
    'ServiceRequest::BusinessCardCreation': {
      phone_number: z.string().min(1, { message }),
      email: z.string().min(1, { message }),
      qr_code_link: z.string().optional(),
      industry: z.string().min(1, { message }),
      colors: z.object({
        primary: hsvaSchema,
        secondary: hsvaSchema,
      }),
      style: z.string().min(1, { message }),
      job_title: z.string().min(1, { message }),
      website_url: z.string().min(1, { message }),
      linkedin_url: z.string().min(1, { message }),
    },
  }

  const schema = {
    ...(serviceRequestType !== 'ResumeRevision::CandidateRevision' &&
    serviceRequestType !== 'ServiceRequest::BusinessCardCreation'
      ? { message: z.string().min(1, { message }) }
      : { message: z.string().optional() }),
    ...schemaMap[serviceRequestType],
  }

  // Default HSVA values for colors
  const DEFAULT_PRIMARY_HSVA = { h: 231, s: 78, v: 49, a: 1 } // #1a237e
  const DEFAULT_SECONDARY_HSVA = { h: 0, s: 0, v: 38, a: 1 } // #616161
  const defaultValues = {
    document_id: '',
    message: serviceRequest?.message || '',
    linkedin_url:
      current_user.candidate_linkedin_url || serviceRequest?.linkedin_url || '',
    resume_document_id: '',
    seniority_level: current_user.candidate_seniority_level || '',
    example_job_1: serviceRequest?.example_job_1 || '',
    example_job_2: serviceRequest?.example_job_2 || '',
    example_company_1: serviceRequest?.example_company_1 || '',
    example_company_2: serviceRequest?.example_company_2 || '',
    notes: serviceRequest?.notes || '',
    remote_content: null,
    industry_role: '',
    location: '',
    industry: serviceRequest?.industry || '',
    industry_2: serviceRequest?.industry_2 || '',
    salary: '',
    sector: serviceRequest?.sector || '',
    company_size: serviceRequest?.company_size || '',
    desired_goal: current_user.candidate_desired_goal || '',
    desired_timeline: current_user.candidate_desired_timeline
      ? String(current_user.candidate_desired_timeline)
      : '',
    target_roles:
      serviceRequest?.target_roles || current_user.candidate_target_role || '',
    career_level: serviceRequest?.career_level || '',
    interview_types: serviceRequest?.interview_types || [],
    target_job_description: serviceRequest?.target_job_description || '',
    interview_with: serviceRequest?.interview_with || '',
    interview_type: serviceRequest?.interview_type || '',
    most_recent_role: serviceRequest?.most_recent_role || '',
    desired_role: serviceRequest?.desired_role || '',
    phone_number: serviceRequest?.phone_number || '',
    email: serviceRequest?.email || '',
    qr_code: serviceRequest?.qr_code || false,
    colors: {
      primary: DEFAULT_PRIMARY_HSVA,
      secondary: DEFAULT_SECONDARY_HSVA,
    },
    style: serviceRequest?.style || '',
    general_topic: serviceRequest?.general_topic || '',
    job_title: serviceRequest?.job_title || '',
    website_url: serviceRequest?.website_url || '',
    work_experience: serviceRequest?.work_experience || '',
    education_history: serviceRequest?.education_history || '',
    achievements: serviceRequest?.achievements || '',
    skills_certifications: serviceRequest?.skills_certifications || '',
  }

  const formsThatRequireFileUpload = [
    'ServiceRequest::ResumeReview',
    'ServiceRequest::ExecutiveBio',
    'ServiceRequest::MockInterview',
  ]

  const isValid = (data) => {
    if (formsThatRequireFileUpload.includes(serviceRequestType)) {
      const valid = data.document_id || data.resume_document_id
      return valid
    }
    return true
  }
  const form = useForm({
    resolver: zodResolver(
      z.object(schema).refine((data) => isValid(data), {
        message,
        path: ['document_id'], // Highlight `document_id` in case of an error
      }),
    ),
    defaultValues,
  })

  const serviceRequestData = Object.keys(schema).reduce((acc, field) => {
    let value = form.getValues(field)
    if (field === 'colors' && value) {
      acc.colors = {
        primary: hsvaToHex(value.primary),
        secondary: hsvaToHex(value.secondary),
      }
    } else if (
      field === 'remote_content' ||
      (value !== null && value !== undefined && value !== '')
    ) {
      acc[field] = value
    }
    return acc
  }, {})

  const clearForm = () => {
    form.reset(defaultValues)
    setSubmitted(false)
    setShowBackButton(true)
    setPage(1)
  }

  const onSubmit = () => {
    setSubmitted(true)
    if (meetingViaCalendly) {
      handleNext()
    } else {
      createServiceRequest()
    }
  }

  return {
    serviceRequestForm: form,
    onSubmit,
    clearForm,
    submitted,
    handleBack,
    serviceRequestData,
    setSubmitted,
  }
}
export default useServiceRequestForm
